import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { API } from '../../config/api';

const US_STATES = [
  'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut','Delaware',
  'Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa','Kansas','Kentucky',
  'Louisiana','Maine','Maryland','Massachusetts','Michigan','Minnesota','Mississippi',
  'Missouri','Montana','Nebraska','Nevada','New Hampshire','New Jersey','New Mexico',
  'New York','North Carolina','North Dakota','Ohio','Oklahoma','Oregon','Pennsylvania',
  'Rhode Island','South Carolina','South Dakota','Tennessee','Texas','Utah','Vermont',
  'Virginia','Washington','West Virginia','Wisconsin','Wyoming','District of Columbia',
];

const WaitlistModal = ({ isOpen, onClose, planName }) => {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [planInterest, setPlanInterest] = useState(planName || 'Growth');
  const [propertyCount, setPropertyCount] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');
  const [showStateDropdown, setShowStateDropdown] = useState(false);

  const propertyRanges = ['1-5', '6-15', '16-50', '50+'];

  // Sync planInterest when planName prop changes
  React.useEffect(() => {
    if (planName) setPlanInterest(planName);
  }, [planName]);

  const formatPhone = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const handlePhoneChange = (e) => {
    setPhone(formatPhone(e.target.value));
  };

  const handleSubmit = async () => {
    setError('');

    if (!fullName.trim()) return setError('Please enter your name');
    if (!email.trim() || !email.includes('@')) return setError('Please enter a valid email');
    const phoneDigits = phone.replace(/\D/g, '');
    if (phoneDigits.length < 10) return setError('Please enter a valid 10-digit phone number');
    if (!city.trim()) return setError('Please enter your city');
    if (!state) return setError('Please select your state');

    try {
      setIsLoading(true);
      const response = await axios.post(`${API.BASE_URL}/waitlist`, {
        full_name: fullName.trim(),
        email: email.trim(),
        phone: phoneDigits,
        plan_interest: planInterest,
        property_count: propertyCount || null,
        city: city.trim(),
        state: state,
        message: message.trim() || null,
      });
      setSuccessMessage(response.data.message);
      setShowSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFullName('');
    setEmail('');
    setPhone('');
    setPlanInterest(planName || 'Growth');
    setPropertyCount('');
    setCity('');
    setState('');
    setMessage('');
    setError('');
    setShowSuccess(false);
    setShowStateDropdown(false);
    onClose();
  };

  if (!isOpen) return null;

  const inputClass = "w-full px-3 py-2.5 rounded-lg border border-warm-border text-sm text-warm-text placeholder:text-warm-textMuted focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand/20 transition-colors";

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[999] flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/40 backdrop-blur-sm"
          onClick={handleClose}
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 10 }}
          className="relative bg-white rounded-2xl shadow-xl w-full max-w-[440px] max-h-[90vh] overflow-y-auto"
        >
          {/* Header */}
          <div className="px-6 pt-6 pb-4">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-serif text-xl font-bold text-warm-text">
                Join the waitlist
              </h3>
              <button
                onClick={handleClose}
                className="text-warm-textMuted hover:text-warm-text transition-colors text-xl leading-none"
              >
                ×
              </button>
            </div>
            <p className="text-[13px] text-warm-textSub">
              We're building advanced tools for landlords. Be the first to know when they're ready.
            </p>
          </div>

          {showSuccess ? (
            <div className="px-6 pb-6">
              <div className="bg-[#F0FAF4] border border-[#D1E9DA] rounded-xl p-5 text-center">
                <span className="text-3xl mb-3 block">🎉</span>
                <p className="text-sm font-semibold text-warm-text mb-1">You're on the list!</p>
                <p className="text-xs text-warm-textSub">{successMessage}</p>
              </div>
              <button
                onClick={handleClose}
                className="w-full mt-4 py-3 rounded-lg text-sm font-semibold bg-brand text-white hover:bg-brand-dark transition-colors"
              >
                Done
              </button>
            </div>
          ) : (
            <div className="px-6 pb-6">
              {error && (
                <div className="mb-3 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-xs text-red-600">
                  {error}
                </div>
              )}

              {/* Full name */}
              <div className="mb-3">
                <label className="block text-xs font-semibold text-warm-text mb-1">
                  Full name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Jane Smith"
                  className={inputClass}
                />
              </div>

              {/* Email */}
              <div className="mb-3">
                <label className="block text-xs font-semibold text-warm-text mb-1">
                  Email <span className="text-red-400">*</span>
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="jane@example.com"
                  className={inputClass}
                />
              </div>

              {/* Phone with fixed +1 prefix */}
              <div className="mb-3">
                <label className="block text-xs font-semibold text-warm-text mb-1">
                  Phone <span className="text-red-400">*</span>
                </label>
                <div className="flex">
                  <div className="flex items-center px-3 py-2.5 rounded-l-lg border border-r-0 border-warm-border bg-warm-bgAlt text-sm text-warm-textMuted select-none">
                    🇺🇸 +1
                  </div>
                  <input
                    type="tel"
                    value={phone}
                    onChange={handlePhoneChange}
                    placeholder="(555) 000-0000"
                    className="flex-1 px-3 py-2.5 rounded-r-lg border border-warm-border text-sm text-warm-text placeholder:text-warm-textMuted focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand/20 transition-colors"
                  />
                </div>
              </div>

              {/* Plan interest dropdown */}
              <div className="mb-3">
                <label className="block text-xs font-semibold text-warm-text mb-1">
                  Plan you're interested in <span className="text-red-400">*</span>
                </label>
                <select
                  value={planInterest}
                  onChange={(e) => setPlanInterest(e.target.value)}
                  className={`${inputClass} appearance-none bg-white bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2012%2012%22%3E%3Cpath%20fill%3D%22%23999%22%20d%3D%22M6%208L1%203h10z%22%2F%3E%3C%2Fsvg%3E')] bg-no-repeat bg-[right_12px_center]`}
                >
                  <option value="Growth">Growth — $29/mo</option>
                  <option value="Pro">Pro — $79/mo</option>
                </select>
              </div>

              {/* Property count */}
              <div className="mb-3">
                <label className="block text-xs font-semibold text-warm-text mb-1">
                  How many properties do you manage?
                </label>
                <div className="flex gap-2">
                  {propertyRanges.map((range) => (
                    <button
                      key={range}
                      type="button"
                      onClick={() => setPropertyCount(range === propertyCount ? '' : range)}
                      className={`flex-1 py-2 rounded-lg text-xs font-medium transition-colors border ${
                        propertyCount === range
                          ? 'bg-brand/10 border-brand text-brand'
                          : 'border-warm-border text-warm-textSub hover:border-warm-textMuted'
                      }`}
                    >
                      {range}
                    </button>
                  ))}
                </div>
              </div>

              {/* City + State row */}
              <div className="mb-3">
                <label className="block text-xs font-semibold text-warm-text mb-1">
                  Where are your rentals located? <span className="text-red-400">*</span>
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="City"
                    className={`flex-1 ${inputClass}`}
                  />
                  <div className="relative w-[140px]">
                    <button
                      type="button"
                      onClick={() => setShowStateDropdown(!showStateDropdown)}
                      className={`w-full px-3 py-2.5 rounded-lg border text-sm text-left transition-colors ${
                        state
                          ? 'border-warm-border text-warm-text'
                          : 'border-warm-border text-warm-textMuted'
                      } focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand/20`}
                    >
                      {state || 'State'}
                      <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-warm-textMuted text-[10px]">▼</span>
                    </button>
                    {showStateDropdown && (
                      <div className="absolute z-50 mt-1 w-full max-h-[180px] overflow-y-auto bg-white border border-warm-border rounded-lg shadow-lg">
                        {US_STATES.map((s) => (
                          <button
                            key={s}
                            type="button"
                            onClick={() => { setState(s); setShowStateDropdown(false); }}
                            className={`w-full text-left px-3 py-2 text-xs hover:bg-warm-bgAlt transition-colors ${
                              state === s ? 'bg-brand/10 text-brand font-medium' : 'text-warm-text'
                            }`}
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Message */}
              <div className="mb-4">
                <label className="block text-xs font-semibold text-warm-text mb-1">
                  What features are you most looking forward to? <span className="text-warm-textMuted font-normal">(optional)</span>
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="e.g. automated rent reminders, expense tracking..."
                  rows={2}
                  className={`${inputClass} resize-none`}
                />
              </div>

              {/* Submit */}
              <button
                onClick={handleSubmit}
                disabled={isLoading}
                className="w-full py-3 rounded-lg text-sm font-semibold bg-brand text-white hover:bg-brand-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Joining...' : 'Join the waitlist'}
              </button>

              <p className="text-[10px] text-warm-textMuted text-center mt-3 leading-relaxed">
                By submitting, you agree to receive text messages from EzDaily about your waitlist status.
                Message frequency varies. Msg & data rates may apply. Reply STOP to opt out at any time.
                View our{' '}
                <a href="/privacy" className="underline hover:text-warm-textSub">Privacy Policy</a>.
              </p>
            </div>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default WaitlistModal;
